import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CampaignIcon from "@mui/icons-material/Campaign";
import TimelineIcon from "@mui/icons-material/Timeline";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import BoltIcon from "@mui/icons-material/Bolt";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CircleIcon from "@mui/icons-material/Circle";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../axiosConfig";
import ActivityTimeline from "./ActivityTimeline";

const DAY_OPTIONS = [7, 30, 90];
const REPORT_CHANNELS = ["all", "email", "whatsapp", "voice", "inquiry", "other"];
const REPORT_MODULES = ["all", "inquiries", "omnichannel", "mass_email", "whatsapp_broadcast", "tasks", "contacts", "ui"];
const DEFAULT_ALERT_SETTINGS = {
  offline_hours_warning: 2,
  longest_offline_stretch_hours_warning: 1,
  available_without_output_hours_warning: 2,
  status_switches_warning: 6,
};
const DEFAULT_SHIFT_SETTINGS = {
  timezone: "Africa/Harare",
  defaults: {
    workdays: [1, 2, 3, 4, 5],
    start_time: "08:00",
    end_time: "17:00",
    grace_minutes: 15,
  },
  roles: {
    consultant: {
      workdays: [1, 2, 3, 4, 5],
      start_time: "08:00",
      end_time: "17:00",
      grace_minutes: 15,
    },
    marketing: {
      workdays: [1, 2, 3, 4, 5],
      start_time: "08:00",
      end_time: "17:00",
      grace_minutes: 15,
    },
  },
  user_overrides: [],
};
const DEFAULT_ALERT_DELIVERY_SETTINGS = {
  in_app_enabled: true,
  email_enabled: false,
  late_start_days_warning: 2,
  no_activity_shift_days_warning: 1,
  productivity_drop_percent_warning: 30,
};
const WORKDAY_OPTIONS = [
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
  { value: 0, label: "Sun" },
];
const TABS = [
  { value: "manager", label: "Manager View", role: "all", icon: <QueryStatsIcon fontSize="small" /> },
  { value: "consultants", label: "Consultants", role: "consultant", icon: <PeopleAltIcon fontSize="small" /> },
  { value: "marketing", label: "Marketing", role: "marketing", icon: <CampaignIcon fontSize="small" /> },
  { value: "timeline", label: "Timeline", role: "all", icon: <TimelineIcon fontSize="small" /> },
];

const STATUS_META = {
  available: { label: "Available", color: "#16a34a", tone: "rgba(22,163,74,0.14)" },
  offline: { label: "Offline", color: "#64748b", tone: "rgba(100,116,139,0.16)" },
};

const WATCHLIST_META = {
  inactive: {
    title: "Inactive / quiet",
    subtitle: "People with very little meaningful workload in this window.",
    accent: "#64748b",
    empty: "Everyone has active work recorded in this window.",
  },
  offline: {
    title: "Offline watch",
    subtitle: "Team members spending the most time away from active availability.",
    accent: "#ea580c",
    empty: "No one is heavily offline in this window.",
  },
  available_no_output: {
    title: "Available, no output",
    subtitle: "People who stayed present for long stretches without handled workload landing.",
    accent: "#0f766e",
    empty: "No present-without-output signal is currently flagged.",
  },
  campaign_risk: {
    title: "Campaign risk",
    subtitle: "Strong send output paired with weak click results.",
    accent: "#c2410c",
    empty: "No campaign-risk signal is currently flagged.",
  },
  task_gap: {
    title: "Task gap",
    subtitle: "People staying active but not completing assigned consultant tasks.",
    accent: "#7c3aed",
    empty: "No consultant task-completion gaps are currently flagged.",
  },
  overloaded: {
    title: "Overloaded",
    subtitle: "Highest handled volume and activity pressure across the team.",
    accent: "#7c3aed",
    empty: "No overload signal is currently flagged.",
  },
  late_starts: {
    title: "Late starts",
    subtitle: "Scheduled days where people consistently came online after their shift start.",
    accent: "#dc2626",
    empty: "No repeated late-start pattern is currently flagged.",
  },
  no_shift_activity: {
    title: "No shift activity",
    subtitle: "Scheduled shift days that passed without any tracked work activity.",
    accent: "#0f766e",
    empty: "No scheduled no-activity shift pattern is currently flagged.",
  },
  productivity_drop: {
    title: "Productivity drop",
    subtitle: "Handled workload has dropped compared with the earlier part of the selected window.",
    accent: "#be123c",
    empty: "No meaningful productivity drop is currently flagged.",
  },
};

const COLORS = {
  email: "#2563eb",
  whatsapp: "#16a34a",
  calls: "#f59e0b",
  closures: "#ef4444",
  campaign: "#0891b2",
  availability: "#0f766e",
  consultants: "#16a34a",
  marketing: "#2563eb",
};

const PANEL_RADIUS = "21px";
const INNER_RADIUS = "17px";
const PANEL_BORDER = "1px solid rgba(148,163,184,0.18)";
const CARD_SHADOW = "0 14px 34px rgba(15,23,42,0.04)";
const SUMMARY_GRID_SX = {
  display: "grid",
  gap: 2.5,
  alignItems: "stretch",
  gridAutoRows: "1fr",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(4, minmax(0, 1fr))",
  },
};
const DASHBOARD_GRID_SX = {
  display: "grid",
  gap: 2.5,
  alignItems: "stretch",
  gridAutoRows: "1fr",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, minmax(0, 1fr))",
    lg: "repeat(3, minmax(0, 1fr))",
    xl: "repeat(5, minmax(0, 1fr))",
  },
};
const panelSx = {
  p: { xs: 2.25, md: 2.75 },
  width: "100%",
  borderRadius: PANEL_RADIUS,
  border: PANEL_BORDER,
  bgcolor: "#ffffff",
  boxShadow: CARD_SHADOW,
};
const softPanelSx = {
  borderRadius: INNER_RADIUS,
  border: "1px solid rgba(148,163,184,0.16)",
  bgcolor: "rgba(248,250,252,0.82)",
};

const formatNumber = (value) => Number(value || 0).toLocaleString();
const formatHours = (value) => `${Number(value || 0).toFixed(1)}h`;
const statusMeta = (status) => STATUS_META[status] || STATUS_META.offline;

const formatDateTime = (value) => {
  if (!value) return "No activity yet";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "No activity yet";

  return parsed.toLocaleString([], {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function SummaryCard({ label, value, subtitle, accent, icon }) {
  const displayValue = String(value ?? "0");
  const isLargeLabel = displayValue.length > 14;

  return (
    <Paper
      sx={{
        ...panelSx,
        width: "100%",
        p: { xs: 2.25, md: 2.5 },
        minHeight: { xs: 176, md: 192 },
        background: `linear-gradient(145deg, ${accent}12, #ffffff)`,
        border: `1px solid ${accent}22`,
      }}
    >
      <Stack spacing={1.6} sx={{ height: "100%" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: "15px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${accent}16`,
              color: accent,
            }}
          >
            {icon}
          </Box>
          <TrendingUpIcon sx={{ color: `${accent}a6`, fontSize: 22 }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ color: "text.secondary", fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: isLargeLabel ? "1.95rem" : "2.55rem", md: isLargeLabel ? "2.2rem" : "2.85rem" },
              fontWeight: 900,
              lineHeight: 1.02,
              letterSpacing: "-0.03em",
              overflowWrap: "anywhere",
            }}
          >
            {displayValue}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            lineHeight: 1.55,
            minHeight: { xs: "auto", md: 42 },
          }}
        >
          {subtitle}
        </Typography>
      </Stack>
    </Paper>
  );
}

function AlertSettingsCard({ settings, saving, onChange, onSave }) {
  const fields = [
    { key: "offline_hours_warning", label: "Offline after", suffix: "hours" },
    { key: "longest_offline_stretch_hours_warning", label: "Longest offline", suffix: "hours" },
    { key: "available_without_output_hours_warning", label: "Available but idle", suffix: "hours" },
    { key: "status_switches_warning", label: "Status switches", suffix: "switches" },
  ];

  return (
    <Paper sx={{ ...panelSx, width: "100%" }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Alert Preferences
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            These thresholds drive the manager watchlists and individual productivity alerts for this tenant.
          </Typography>
        </Box>

        <Grid container spacing={1.75}>
          {fields.map((field) => (
            <Grid key={field.key} item xs={12} sm={6} md={4} xl={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={field.label}
                value={settings?.[field.key] ?? ""}
                onChange={(event) => onChange(field.key, event.target.value)}
                inputProps={{
                  min: field.key === "status_switches_warning" ? 1 : 0.25,
                  max: field.key === "status_switches_warning" ? 100 : 24,
                  step: field.key === "status_switches_warning" ? 1 : 0.25,
                }}
                helperText={field.suffix}
              />
            </Grid>
          ))}
        </Grid>

        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Save once and the whole manager watchlist will follow these thresholds.
          </Typography>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving}
            sx={{ borderRadius: "15px", px: 2.4, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
          >
            {saving ? "Saving..." : "Save Alert Preferences"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ShiftProfileFields({ profile, onChange, compact = false }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={compact ? 6 : 3}>
        <TextField
          fullWidth
          size="small"
          label="Start"
          value={profile?.start_time || ""}
          onChange={(event) => onChange("start_time", event.target.value)}
          helperText="HH:MM"
        />
      </Grid>
      <Grid item xs={12} sm={compact ? 6 : 3}>
        <TextField
          fullWidth
          size="small"
          label="End"
          value={profile?.end_time || ""}
          onChange={(event) => onChange("end_time", event.target.value)}
          helperText="HH:MM"
        />
      </Grid>
      <Grid item xs={12} sm={compact ? 6 : 2}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Grace"
          value={profile?.grace_minutes ?? 0}
          onChange={(event) => onChange("grace_minutes", event.target.value)}
          helperText="minutes"
          inputProps={{ min: 0, max: 180, step: 5 }}
        />
      </Grid>
      <Grid item xs={12} sm={compact ? 6 : 4}>
        <TextField
          fullWidth
          select
          size="small"
          label="Workdays"
          value={profile?.workdays || []}
          onChange={(event) => onChange("workdays", event.target.value)}
          SelectProps={{
            multiple: true,
            renderValue: (selected) =>
              selected
                .map((value) => WORKDAY_OPTIONS.find((option) => Number(option.value) === Number(value))?.label || value)
                .join(", "),
          }}
          helperText="Scheduled working days"
        >
          {WORKDAY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
}

function ShiftSettingsCard({
  settings,
  members,
  saving,
  onTimezoneChange,
  onProfileChange,
  onOverrideProfileChange,
  onAddOverride,
  onRemoveOverride,
  onSave,
}) {
  return (
    <Paper sx={{ ...panelSx, width: "100%" }}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Shift Scheduling
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Define default working hours, team-level schedules, and specific member overrides for shift-aware analytics.
          </Typography>
        </Box>

        <Grid container spacing={1.75}>
          <Grid item xs={12} lg={4}>
            <TextField
              fullWidth
              size="small"
              label="Timezone"
              value={settings?.timezone || ""}
              onChange={(event) => onTimezoneChange(event.target.value)}
              helperText="Used for shift comparisons"
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ ...softPanelSx, p: 2.25 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
            Default Shift
          </Typography>
          <ShiftProfileFields profile={settings?.defaults} onChange={(key, value) => onProfileChange("defaults", key, value)} />
        </Paper>

        <Grid container spacing={1.75}>
          <Grid item xs={12} lg={6}>
            <Paper variant="outlined" sx={{ ...softPanelSx, p: 2.25, height: "100%" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                Consultant Team Shift
              </Typography>
              <ShiftProfileFields profile={settings?.roles?.consultant} onChange={(key, value) => onProfileChange("consultant", key, value)} compact />
            </Paper>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Paper variant="outlined" sx={{ ...softPanelSx, p: 2.25, height: "100%" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 1.5 }}>
                Marketing Team Shift
              </Typography>
              <ShiftProfileFields profile={settings?.roles?.marketing} onChange={(key, value) => onProfileChange("marketing", key, value)} compact />
            </Paper>
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ ...softPanelSx, p: 2.25 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" sx={{ mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Member Overrides
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Use overrides when one person follows a different shift from their team default.
              </Typography>
            </Box>
            <Button variant="outlined" onClick={onAddOverride} sx={{ borderRadius: "15px" }}>
              Add Override
            </Button>
          </Stack>

          {settings?.user_overrides?.length ? (
            <Stack spacing={2}>
              {settings.user_overrides.map((override, index) => (
                <Paper key={`${override.user_id || "new"}-${index}`} sx={{ ...softPanelSx, p: 2 }}>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
                      <TextField
                        select
                        size="small"
                        label="Team member"
                        value={override.user_id || ""}
                        onChange={(event) => onOverrideProfileChange(index, "user_id", event.target.value)}
                        sx={{ minWidth: 240 }}
                      >
                        {members.map((member) => (
                          <MenuItem key={member.id} value={member.id}>
                            {member.name}
                          </MenuItem>
                        ))}
                      </TextField>
                      <Button color="error" onClick={() => onRemoveOverride(index)}>
                        Remove
                      </Button>
                    </Stack>
                    <ShiftProfileFields profile={override} onChange={(key, value) => onOverrideProfileChange(index, key, value)} compact />
                  </Stack>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Alert severity="info">No individual shift overrides are configured yet.</Alert>
          )}
        </Paper>

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving}
            sx={{ borderRadius: "15px", px: 2.4, bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" } }}
          >
            {saving ? "Saving..." : "Save Shift Settings"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function AlertDeliverySettingsCard({ settings, saving, onChange, onSave }) {
  return (
    <Paper sx={panelSx}>
      <Stack spacing={2.5}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Alert Delivery
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Control which manager alerts are surfaced in-app, emailed, and when repeated shift-discipline patterns should escalate.
          </Typography>
        </Box>

        <Grid container spacing={1.75}>
          <Grid item xs={12} sm={6} md={4} xl={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="In-app alerts"
              value={String(Boolean(settings?.in_app_enabled))}
              onChange={(event) => onChange("in_app_enabled", event.target.value === "true")}
            >
              <MenuItem value="true">Enabled</MenuItem>
              <MenuItem value="false">Muted</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Email alerts"
              value={String(Boolean(settings?.email_enabled))}
              onChange={(event) => onChange("email_enabled", event.target.value === "true")}
            >
              <MenuItem value="true">Enabled</MenuItem>
              <MenuItem value="false">Muted</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Late-start days"
              value={settings?.late_start_days_warning ?? 2}
              onChange={(event) => onChange("late_start_days_warning", event.target.value)}
              helperText="days"
              inputProps={{ min: 1, max: 14 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="No-activity days"
              value={settings?.no_activity_shift_days_warning ?? 1}
              onChange={(event) => onChange("no_activity_shift_days_warning", event.target.value)}
              helperText="days"
              inputProps={{ min: 1, max: 14 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} xl={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Productivity drop"
              value={settings?.productivity_drop_percent_warning ?? 30}
              onChange={(event) => onChange("productivity_drop_percent_warning", event.target.value)}
              helperText="percent drop"
              inputProps={{ min: 5, max: 100 }}
            />
          </Grid>
        </Grid>

        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Choose how quickly repeated discipline patterns should surface to managers.
          </Typography>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={saving}
            sx={{ borderRadius: "15px", px: 2.4, bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}
          >
            {saving ? "Saving..." : "Save Delivery Settings"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function LiveAlertsPanel({ alerts, loading, onAcknowledge, onAcknowledgeAll, acknowledgingAll }) {
  const [query, setQuery] = useState("");
  const [visibility, setVisibility] = useState("open");
  const normalizedQuery = query.trim().toLowerCase();
  const openCount = (alerts || []).filter((alertItem) => !alertItem.acknowledged_at).length;
  const acknowledgedCount = Math.max((alerts || []).length - openCount, 0);
  const filteredAlerts = (alerts || []).filter((alertItem) => {
    const matchesVisibility =
      visibility === "all"
        ? true
        : visibility === "acknowledged"
          ? Boolean(alertItem.acknowledged_at)
          : !alertItem.acknowledged_at;

    if (!matchesVisibility) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    const searchableText = [
      alertItem.title,
      alertItem.message,
      alertItem.subject_user?.name,
      alertItem.subject_user?.email,
      alertItem.code,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedQuery);
  });

  return (
    <Paper sx={panelSx}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
            Live Alerts
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            These are the persisted manager alerts generated by the shift-aware analytics engine.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${openCount} open`} color={openCount > 0 ? "warning" : "success"} size="small" />
          <Chip label={`${acknowledgedCount} acknowledged`} variant="outlined" size="small" />
          <Button
            size="small"
            variant="contained"
            onClick={onAcknowledgeAll}
            disabled={acknowledgingAll || openCount === 0}
            sx={{ borderRadius: "15px", bgcolor: "#0f766e", "&:hover": { bgcolor: "#115e59" } }}
          >
            {acknowledgingAll ? "Acknowledging..." : `Acknowledge ${openCount} open`}
          </Button>
        </Stack>
      </Stack>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={7}>
          <TextField
            fullWidth
            size="small"
            label="Search alerts"
            placeholder="Search by title, member, email, or code"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            select
            size="small"
            label="Visibility"
            value={visibility}
            onChange={(event) => setVisibility(event.target.value)}
          >
            <MenuItem value="open">Open alerts</MenuItem>
            <MenuItem value="acknowledged">Acknowledged</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        </Grid>
      </Grid>
      {loading ? (
        <Stack spacing={1.5}>
          <Skeleton variant="rounded" height={76} />
          <Skeleton variant="rounded" height={76} />
        </Stack>
      ) : filteredAlerts.length ? (
        <Stack spacing={1.5}>
          {filteredAlerts.map((alertItem) => (
            <Alert
              key={alertItem.id}
              severity={alertItem.level === "success" ? "success" : alertItem.level === "info" ? "info" : "warning"}
              action={
                <Button size="small" color="inherit" onClick={() => onAcknowledge(alertItem.id)} disabled={Boolean(alertItem.acknowledged_at)}>
                  {alertItem.acknowledged_at ? "Acknowledged" : "Acknowledge"}
                </Button>
              }
              sx={{ alignItems: "center", borderRadius: INNER_RADIUS }}
            >
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {alertItem.title}
              </Typography>
              <Typography variant="body2">{alertItem.message}</Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.4 }}>
                {alertItem.subject_user?.name ? `${alertItem.subject_user.name} • ` : ""}
                {formatDateTime(alertItem.last_triggered_at)}
              </Typography>
            </Alert>
          ))}
        </Stack>
      ) : alerts?.length ? (
        <Alert severity="info">No alerts match the current search and visibility filters.</Alert>
      ) : (
        <Alert severity="success">No active manager alerts are open right now.</Alert>
      )}
    </Paper>
  );
}

function TrendChart({ title, description, data, lines }) {
  return (
    <Paper sx={panelSx}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        {description}
      </Typography>
      <Box sx={{ height: { xs: 280, md: 320 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
            <RechartsTooltip />
            <Legend />
            {lines.map((line) => (
              <Line
                key={line.key}
                type="monotone"
                dataKey={line.key}
                name={line.label}
                stroke={line.color}
                strokeWidth={line.strokeWidth || 3}
                strokeDasharray={line.dashed ? "6 6" : undefined}
                dot={line.dot === false ? false : { r: 2 }}
                activeDot={line.dot === false ? false : { r: 5 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

function StatusBreakdown({ statusCounts }) {
  const total = Object.values(statusCounts || {}).reduce((sum, count) => sum + Number(count || 0), 0);

  return (
    <Paper sx={panelSx}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        Availability Snapshot
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Live automatic presence mix from the unified inbox availability layer.
      </Typography>
      <Stack spacing={2}>
        {Object.entries(STATUS_META).map(([status, meta]) => {
          const count = Number(statusCounts?.[status] || 0);
          const share = total > 0 ? Math.round((count / total) * 100) : 0;

          return (
            <Box key={status}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircleIcon sx={{ fontSize: 12, color: meta.color }} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {meta.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {count} people
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={share}
                sx={{
                  height: 10,
                  borderRadius: 999,
                  bgcolor: meta.tone,
                  "& .MuiLinearProgress-bar": {
                    bgcolor: meta.color,
                    borderRadius: 999,
                  },
                }}
              />
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function ActivityFeed({ title, description, items }) {
  return (
    <Paper sx={panelSx}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        {description}
      </Typography>
      {items?.length ? (
        <Stack spacing={1.5}>
          {items.map((item) => {
            const actor = item.actor?.name || "System";
            const initials = actor.charAt(0).toUpperCase();
            const actorRole = item.actor?.role || "team";

            return (
              <Box
                key={item.id}
                sx={{
                  p: 1.75,
                  borderRadius: INNER_RADIUS,
                  bgcolor: "rgba(248,250,252,0.9)",
                  border: "1px solid rgba(148,163,184,0.18)",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Avatar sx={{ width: 36, height: 36, bgcolor: actorRole === "marketing" ? "#2563eb" : "#16a34a" }}>
                    {initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {actor}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.35 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.4 }}>
                      {item.subtitle || "Tracked system activity"}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                    {formatDateTime(item.created_at)}
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Alert severity="info">No activity has been recorded for this window yet.</Alert>
      )}
    </Paper>
  );
}

function WatchlistsSection({ watchlists, onSelectMember }) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2.5,
        alignItems: "stretch",
        gridTemplateColumns: "1fr",
      }}
    >
      {Object.entries(WATCHLIST_META).map(([key, meta]) => (
        <Paper key={key} sx={{ ...panelSx, width: "100%", height: "100%" }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
              {meta.subtitle}
            </Typography>
            {watchlists?.[key]?.length ? (
              <Stack spacing={1.5}>
                {watchlists[key].map((item) => {
                  const currentStatus = statusMeta(item.current_status);

                  return (
                    <Box
                      key={`${key}-${item.id}`}
                      sx={{
                        p: 1.6,
                        borderRadius: INNER_RADIUS,
                        border: "1px solid rgba(148,163,184,0.18)",
                        bgcolor: `${meta.accent}0d`,
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {item.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.35 }}>
                            {item.email}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.8 }}>
                            {item.reason}
                          </Typography>
                        </Box>
                        <Stack spacing={0.8} alignItems="flex-end">
                          <Chip
                            size="small"
                            label={currentStatus.label}
                            sx={{
                              bgcolor: currentStatus.tone,
                              color: currentStatus.color,
                              fontWeight: 700,
                            }}
                          />
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {item.metric}: {String(item.value)}
                          </Typography>
                          <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => onSelectMember(item)} sx={{ borderRadius: "13px" }}>
                            Open
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Alert severity="success">{meta.empty}</Alert>
            )}
          </Paper>
      ))}
    </Box>
  );
}

function ProductivityTable({ title, description, members, onSelectMember }) {
  const [query, setQuery] = useState("");
  const [memberFilter, setMemberFilter] = useState("all");
  const [sortBy, setSortBy] = useState("handled_desc");
  const normalizedQuery = query.trim().toLowerCase();
  const filteredMembers = (members || []).filter((member) => {
    const signalCount = Array.isArray(member.signals) ? member.signals.length : 0;
    const matchesFilter =
      memberFilter === "needs_attention"
        ? signalCount > 0
        : memberFilter === "healthy"
          ? signalCount === 0
          : true;

    if (!matchesFilter) {
      return false;
    }

    if (!normalizedQuery) {
      return true;
    }

    const searchableText = [
      member.name,
      member.email,
      member.role,
      member.current_status,
      member.shift_profile_label,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return searchableText.includes(normalizedQuery);
  });
  const visibleMembers = [...filteredMembers].sort((left, right) => {
    switch (sortBy) {
      case "activity_desc":
        return Number(right.activity_events || 0) - Number(left.activity_events || 0);
      case "closures_desc":
        return Number(right.closures || 0) - Number(left.closures || 0);
      case "campaign_desc":
        return Number(right.campaign_output_total || 0) - Number(left.campaign_output_total || 0);
      case "available_desc":
        return Number(right.available_hours || 0) - Number(left.available_hours || 0);
      case "offline_desc":
        return Number(right.offline_hours || 0) - Number(left.offline_hours || 0);
      case "shift_attention_desc":
        return (
          Number(right.no_activity_shift_days || 0) +
          Number(right.late_starts_count || 0) -
          (Number(left.no_activity_shift_days || 0) + Number(left.late_starts_count || 0))
        );
      case "last_active_desc":
        return new Date(right.last_activity_at || 0).getTime() - new Date(left.last_activity_at || 0).getTime();
      case "handled_desc":
      default:
        return (
          Number(right.handled_total || 0) - Number(left.handled_total || 0) ||
          Number(right.activity_events || 0) - Number(left.activity_events || 0)
        );
    }
  });

  return (
    <Paper sx={panelSx}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        {description}
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            size="small"
            label="Search team members"
            placeholder="Search by name, email, role, status, or shift"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            select
            size="small"
            label="Member filter"
            value={memberFilter}
            onChange={(event) => setMemberFilter(event.target.value)}
          >
            <MenuItem value="all">All members</MenuItem>
            <MenuItem value="needs_attention">Needs attention</MenuItem>
            <MenuItem value="healthy">Healthy only</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            fullWidth
            select
            size="small"
            label="Sort by"
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            <MenuItem value="handled_desc">Handled workload</MenuItem>
            <MenuItem value="activity_desc">Activity volume</MenuItem>
            <MenuItem value="closures_desc">Closures</MenuItem>
            <MenuItem value="campaign_desc">Campaign output</MenuItem>
            <MenuItem value="available_desc">Available hours</MenuItem>
            <MenuItem value="offline_desc">Offline hours</MenuItem>
            <MenuItem value="shift_attention_desc">Shift attention flags</MenuItem>
            <MenuItem value="last_active_desc">Last activity</MenuItem>
          </TextField>
        </Grid>
      </Grid>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }} useFlexGap>
        <Chip
          size="small"
          label={`Showing ${visibleMembers.length} of ${(members || []).length} members`}
          sx={{ fontWeight: 700 }}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${visibleMembers.filter((member) => (member.signals || []).length > 0).length} with signals`}
        />
      </Stack>
      <TableContainer sx={{ borderRadius: INNER_RADIUS, border: "1px solid rgba(226,232,240,0.9)" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(241,245,249,0.7)" }}>
              <TableCell>Team member</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Claimed</TableCell>
              <TableCell align="right">Messages</TableCell>
              <TableCell align="right">Touches</TableCell>
              <TableCell align="right">Calls</TableCell>
              <TableCell align="right">Closed</TableCell>
              <TableCell align="right">Tasks</TableCell>
              <TableCell align="right">Campaign output</TableCell>
              <TableCell align="right">Shift schedule</TableCell>
              <TableCell align="right">Shift adherence</TableCell>
              <TableCell align="right">Late starts</TableCell>
              <TableCell align="right">No-shift activity days</TableCell>
              <TableCell align="right">Available</TableCell>
              <TableCell align="right">Offline</TableCell>
              <TableCell align="right">Longest offline</TableCell>
              <TableCell align="right">Switches</TableCell>
              <TableCell align="right">Activity</TableCell>
              <TableCell align="right">Last active</TableCell>
              <TableCell align="right">Profile</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleMembers.length ? (
              visibleMembers.map((member) => {
                const meta = statusMeta(member.current_status);
                const signalCount = Array.isArray(member.signals) ? member.signals.length : 0;

                return (
                  <TableRow key={member.id} hover>
                    <TableCell>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar sx={{ width: 34, height: 34, bgcolor: member.role === "marketing" ? "#2563eb" : "#16a34a" }}>
                          {(member.name || "?").charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {member.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {member.email}
                          </Typography>
                          {signalCount > 0 ? (
                            <Chip
                              size="small"
                              label={`${signalCount} signal${signalCount === 1 ? "" : "s"}`}
                              color="warning"
                              sx={{ mt: 0.8, height: 22, fontWeight: 700 }}
                            />
                          ) : null}
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ textTransform: "capitalize" }}>{member.role}</TableCell>
                    <TableCell>
                      <Chip
                        label={meta.label}
                        size="small"
                        sx={{
                          bgcolor: meta.tone,
                          color: meta.color,
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">{formatNumber(member.inquiries_claimed)}</TableCell>
                    <TableCell align="right">{formatNumber(member.messages_answered)}</TableCell>
                    <TableCell align="right">{formatNumber(member.customer_touches)}</TableCell>
                    <TableCell align="right">{formatNumber(member.calls_handled)}</TableCell>
                    <TableCell align="right">{formatNumber(member.closures)}</TableCell>
                    <TableCell align="right">{formatNumber(member.tasks_completed)}</TableCell>
                    <TableCell align="right">{formatNumber(member.campaign_output_total)}</TableCell>
                    <TableCell align="right">{member.shift_profile_label || "Not set"}</TableCell>
                    <TableCell align="right">{Number(member.shift_adherence_rate || 0).toFixed(1)}%</TableCell>
                    <TableCell align="right">{formatNumber(member.late_starts_count)}</TableCell>
                    <TableCell align="right">{formatNumber(member.no_activity_shift_days)}</TableCell>
                    <TableCell align="right">{formatHours(member.available_hours)}</TableCell>
                    <TableCell align="right">{formatHours(member.offline_hours)}</TableCell>
                    <TableCell align="right">{formatHours(member.longest_offline_stretch_hours)}</TableCell>
                    <TableCell align="right">{formatNumber(member.status_switches)}</TableCell>
                    <TableCell align="right">{formatNumber(member.activity_events)}</TableCell>
                    <TableCell align="right">{formatDateTime(member.last_activity_at)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => onSelectMember(member)}>
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={21}>
                  <Alert severity={(members || []).length ? "info" : "warning"}>
                    {(members || []).length
                      ? "No team members matched the current search and filter selection."
                      : "No team members matched this view in the selected time window."}
                  </Alert>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function BreakdownBars({ title, description, items, accent }) {
  const highest = Math.max(...(items?.map((item) => Number(item.quantity || item.events || 0)) || [0]));

  return (
    <Paper sx={{ p: 3, borderRadius: 4, height: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        {description}
      </Typography>
      {items?.length ? (
        <Stack spacing={2}>
          {items.map((item) => {
            const value = Number(item.quantity || item.events || 0);
            const share = highest > 0 ? Math.round((value / highest) * 100) : 0;

            return (
              <Box key={`${title}-${item.key}`}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.8 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {formatNumber(value)}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={share}
                  sx={{
                    height: 10,
                    borderRadius: 999,
                    bgcolor: `${accent}16`,
                    "& .MuiLinearProgress-bar": {
                      bgcolor: accent,
                      borderRadius: 999,
                    },
                  }}
                />
              </Box>
            );
          })}
        </Stack>
      ) : (
        <Alert severity="info">No tracked activity landed in this breakdown yet.</Alert>
      )}
    </Paper>
  );
}

function DetailDrawer({ open, onClose, detail, loading, selectedMember, onExport }) {
  const member = detail?.member || selectedMember;
  const currentStatus = statusMeta(member?.current_status);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", sm: 560 }, maxWidth: "100vw" }}>
        <Box sx={{ p: 3, bgcolor: "rgba(248,250,252,0.95)" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="overline" sx={{ color: "#16a34a", fontWeight: 800, letterSpacing: 1.2 }}>
                Individual Drill-Down
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 900, mt: 0.5 }}>
                {member?.name || "Loading profile"}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.2, flexWrap: "wrap" }}>
                <Chip size="small" label={member?.role || "team"} sx={{ textTransform: "capitalize" }} />
                <Chip
                  size="small"
                  label={currentStatus.label}
                  sx={{
                    bgcolor: currentStatus.tone,
                    color: currentStatus.color,
                    fontWeight: 700,
                  }}
                />
                <Chip size="small" label={`Last active: ${formatDateTime(member?.last_activity_at)}`} />
              </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<DownloadOutlinedIcon />} onClick={onExport} disabled={!member?.id}>
                Report
              </Button>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          {loading && (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={100} />
              <Skeleton variant="rounded" height={260} />
              <Skeleton variant="rounded" height={160} />
            </Stack>
          )}

          {!loading && !detail && <Alert severity="info">This profile is not available yet.</Alert>}

          {!loading && detail && (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Messages Answered"
                    value={formatNumber(detail.member?.messages_answered)}
                    subtitle="Email and WhatsApp replies in this reporting window"
                    accent={COLORS.whatsapp}
                    icon={<BoltIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Closures"
                    value={formatNumber(detail.member?.closures)}
                    subtitle="Closed or resolved workload items"
                    accent={COLORS.closures}
                    icon={<TaskAltIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Customer Touches"
                    value={formatNumber(detail.member?.customer_touches)}
                    subtitle="Tracked outbound call, WhatsApp, and email touches"
                    accent="#2563eb"
                    icon={<PeopleAltIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Campaign Output"
                    value={formatNumber(detail.member?.campaign_output_total)}
                    subtitle="Mass email plus WhatsApp recipients sent"
                    accent={COLORS.campaign}
                    icon={<CampaignIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Tasks Completed"
                    value={formatNumber(detail.member?.tasks_completed)}
                    subtitle="Consultant-assigned tasks completed in this window"
                    accent="#7c3aed"
                    icon={<TaskAltIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Presence Hours"
                    value={formatHours(detail.member?.available_hours)}
                    subtitle={`Automatic presence ${formatHours(detail.member?.available_hours)} total, offline ${formatHours(detail.member?.offline_hours)}`}
                    accent={COLORS.availability}
                    icon={<AccessTimeIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Presence Rhythm"
                    value={formatNumber(detail.member?.status_switches)}
                    subtitle={`Status changes; longest offline stretch ${formatHours(detail.member?.longest_offline_stretch_hours)}`}
                    accent="#ca8a04"
                    icon={<AccessTimeIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="First Online"
                    value={formatDateTime(detail.member?.first_online_at)}
                    subtitle={`Last online ${formatDateTime(detail.member?.last_online_at)}`}
                    accent="#0f766e"
                    icon={<AccessTimeIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Shift Adherence"
                    value={`${Number(detail.member?.shift_adherence_rate || 0).toFixed(1)}%`}
                    subtitle={`${detail.member?.shift_profile_label || "No shift configured"} • ${formatNumber(detail.member?.late_starts_count)} late starts`}
                    accent="#2563eb"
                    icon={<AccessTimeIcon />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SummaryCard
                    label="Shift Activity Gaps"
                    value={formatNumber(detail.member?.no_activity_shift_days)}
                    subtitle={`${formatNumber(detail.member?.no_activity_shift_days)} no-activity shift days`}
                    accent="#be123c"
                    icon={<QueryStatsIcon />}
                  />
                </Grid>
              </Grid>

              {detail.watchlist?.length ? (
                <Stack spacing={1.2}>
                  {detail.watchlist.map((signal, index) => (
                    <Alert key={`${signal.level}-${index}`} severity={signal.level}>
                      {signal.message}
                    </Alert>
                  ))}
                </Stack>
              ) : null}

              <TrendChart
                title="Daily productivity trend"
                description="Output and presence rhythm are shown together so managers can compare work handled against availability behavior."
                data={detail.trend || []}
                lines={[
                  { key: "email_replies", label: "Email", color: COLORS.email },
                  { key: "whatsapp_replies", label: "WhatsApp", color: COLORS.whatsapp },
                  { key: "closures", label: "Closures", color: COLORS.closures },
                  { key: "customer_touches", label: "Customer touches", color: "#2563eb", strokeWidth: 2.5 },
                  { key: "campaign_output", label: "Campaign output", color: COLORS.campaign },
                  { key: "available_hours", label: "Available hours", color: COLORS.availability, strokeWidth: 2.5 },
                  { key: "offline_hours", label: "Offline hours", color: "#64748b", dashed: true, strokeWidth: 2.5 },
                  { key: "status_switches", label: "Status switches", color: "#7c3aed", strokeWidth: 2, dot: false },
                ]}
              />

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <BreakdownBars
                    title="Module mix"
                    description="Where this person spent their tracked workload."
                    items={detail.module_breakdown || []}
                    accent="#2563eb"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <BreakdownBars
                    title="Channel mix"
                    description="Which channels were most active for this person."
                    items={detail.channel_breakdown || []}
                    accent="#16a34a"
                  />
                </Grid>
              </Grid>

              <ActivityFeed
                title="Recent activity"
                description="Latest tracked actions for this person from the activity ledger."
                items={detail.recent_activity || []}
              />
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default function Performance() {
  const [tab, setTab] = useState("manager");
  const [days, setDays] = useState(30);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [reportUserId, setReportUserId] = useState("");
  const [channelFilter, setChannelFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const [alertSettings, setAlertSettings] = useState(DEFAULT_ALERT_SETTINGS);
  const [savingAlertSettings, setSavingAlertSettings] = useState(false);
  const [shiftSettings, setShiftSettings] = useState(DEFAULT_SHIFT_SETTINGS);
  const [savingShiftSettings, setSavingShiftSettings] = useState(false);
  const [alertDeliverySettings, setAlertDeliverySettings] = useState(DEFAULT_ALERT_DELIVERY_SETTINGS);
  const [savingAlertDeliverySettings, setSavingAlertDeliverySettings] = useState(false);
  const [liveAlerts, setLiveAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [acknowledgingAllAlerts, setAcknowledgingAllAlerts] = useState(false);
  const [notice, setNotice] = useState("");
  const [liveStats, setLiveStats] = useState(null);
  const [liveStatsLoading, setLiveStatsLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const seenAlertIdsRef = useRef(new Set());

  const activeTab = TABS.find((item) => item.value === tab) || TABS[0];
  const consultants = payload?.groups?.consultants || null;
  const marketing = payload?.groups?.marketing || null;
  const overall = payload?.overall || null;
  const allMembers = useMemo(
    () =>
      [...(consultants?.members || []), ...(marketing?.members || [])].sort((left, right) => {
        return (
          Number(right.handled_total || 0) - Number(left.handled_total || 0) ||
          Number(right.activity_events || 0) - Number(left.activity_events || 0)
        );
      }),
    [consultants?.members, marketing?.members],
  );
  const scopedMembers = useMemo(() => {
    if (activeTab.role === "consultant") {
      return consultants?.members || [];
    }

    if (activeTab.role === "marketing") {
      return marketing?.members || [];
    }

    return allMembers;
  }, [activeTab.role, allMembers, consultants?.members, marketing?.members]);
  const exportMode =
    reportUserId || channelFilter !== "all" || moduleFilter !== "all" ? "Activity report" : "Summary report";

  const loadPerformance = useCallback(async (requestedDays) => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/analytics/team-performance", {
        params: { days: requestedDays },
      });

      setPayload(response.data?.data || null);
    } catch (requestError) {
      console.error("Failed to load team performance", requestError);
      setError("Could not load team performance right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMemberDetail = useCallback(async (memberId, requestedDays) => {
    setMemberLoading(true);

    try {
      const response = await api.get(`/analytics/team-performance/users/${memberId}`, {
        params: { days: requestedDays },
      });

      setMemberDetail(response.data?.data || null);
    } catch (requestError) {
      console.error("Failed to load member detail", requestError);
      setError("Could not load the individual drill-down right now.");
    } finally {
      setMemberLoading(false);
    }
  }, []);

  const loadAlerts = useCallback(async (refresh = true) => {
    setAlertsLoading(true);

    try {
      const response = await api.get("/analytics/team-performance/alerts", {
        params: { refresh, days, limit: 25 },
      });

      setLiveAlerts(response.data?.data || []);
    } catch (requestError) {
      console.error("Failed to load manager alerts", requestError);
    } finally {
      setAlertsLoading(false);
    }
  }, [days]);

  const loadLiveStats = useCallback(async () => {
    setLiveStatsLoading(true);
    try {
      const response = await api.get("/analytics/live-stats");
      setLiveStats(response.data?.data || null);
    } catch (err) {
      console.error("Failed to load live stats", err);
    } finally {
      setLiveStatsLoading(false);
    }
  }, []);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const response = await api.post("/analytics/team-performance/sync", null, {
        params: { days },
      });
      setSyncResult({ success: true, message: response.data?.message || "Snapshots rebuilt." });
      await loadPerformance(days);
    } catch (err) {
      setSyncResult({ success: false, message: "Rebuild failed — check server logs." });
    } finally {
      setSyncing(false);
    }
  }, [days, loadPerformance]);

  useEffect(() => {
    loadPerformance(days);
  }, [days, loadPerformance]);

  useEffect(() => {
    if (payload?.alert_settings) {
      setAlertSettings({ ...DEFAULT_ALERT_SETTINGS, ...payload.alert_settings });
    }
    if (payload?.shift_settings) {
      setShiftSettings({
        ...DEFAULT_SHIFT_SETTINGS,
        ...payload.shift_settings,
        defaults: { ...DEFAULT_SHIFT_SETTINGS.defaults, ...(payload.shift_settings?.defaults || {}) },
        roles: {
          consultant: {
            ...DEFAULT_SHIFT_SETTINGS.roles.consultant,
            ...(payload.shift_settings?.roles?.consultant || {}),
          },
          marketing: {
            ...DEFAULT_SHIFT_SETTINGS.roles.marketing,
            ...(payload.shift_settings?.roles?.marketing || {}),
          },
        },
        user_overrides: payload.shift_settings?.user_overrides || [],
      });
    }
    if (payload?.alert_delivery_settings) {
      setAlertDeliverySettings({ ...DEFAULT_ALERT_DELIVERY_SETTINGS, ...payload.alert_delivery_settings });
    }
  }, [payload]);

  useEffect(() => {
    loadAlerts(true);
  }, [days, loadAlerts]);

  useEffect(() => {
    loadLiveStats();
    const interval = setInterval(loadLiveStats, 120000);
    return () => clearInterval(interval);
  }, [loadLiveStats]);

  useEffect(() => {
    if (!liveAlerts?.length || typeof window === "undefined" || !("Notification" in window)) {
      return;
    }

    liveAlerts.forEach((alertItem) => {
      if (seenAlertIdsRef.current.has(alertItem.id)) {
        return;
      }

      seenAlertIdsRef.current.add(alertItem.id);

      if (Notification.permission === "granted" && !alertItem.acknowledged_at) {
        try {
          const notification = new Notification(alertItem.title, {
            body: alertItem.message,
            tag: `performance-alert-${alertItem.id}`,
          });
          setTimeout(() => notification.close(), 10000);
        } catch (notificationError) {
          console.warn("Performance notification error", notificationError);
        }
      } else if (Notification.permission === "default") {
        Notification.requestPermission().catch(() => {});
      }
    });
  }, [liveAlerts]);

  useEffect(() => {
    if (reportUserId && !scopedMembers.some((member) => String(member.id) === String(reportUserId))) {
      setReportUserId("");
    }
  }, [reportUserId, scopedMembers]);

  useEffect(() => {
    if (detailOpen && selectedMember?.id) {
      loadMemberDetail(selectedMember.id, days);
    }
  }, [days, detailOpen, selectedMember?.id, loadMemberDetail]);

  const openMember = (member) => {
    setSelectedMember(member);
    setMemberDetail(null);
    setDetailOpen(true);
    loadMemberDetail(member.id, days);
  };

  const handleDownload = async (overrides = {}) => {
    setDownloading(true);

    try {
      const response = await api.get("/analytics/team-performance/report", {
        params: {
          days,
          role: activeTab.role,
          user_id: overrides.userId || reportUserId || undefined,
          channel: overrides.channel || channelFilter,
          module: overrides.module || moduleFilter,
        },
        responseType: "blob",
      });

      const disposition = response.headers["content-disposition"] || "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const filename = filenameMatch?.[1] || `team-performance-${activeTab.role}-${days}d.csv`;
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (downloadError) {
      console.error("Failed to generate report", downloadError);
      setError("Report generation failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const handleAlertSettingChange = (key, value) => {
    setAlertSettings((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleShiftTimezoneChange = (value) => {
    setShiftSettings((current) => ({
      ...current,
      timezone: value,
    }));
  };

  const handleShiftProfileChange = (scope, key, value) => {
    setShiftSettings((current) => {
      if (scope === "defaults") {
        return {
          ...current,
          defaults: {
            ...current.defaults,
            [key]: key === "workdays" ? value.map(Number) : value,
          },
        };
      }

      return {
        ...current,
        roles: {
          ...current.roles,
          [scope]: {
            ...current.roles?.[scope],
            [key]: key === "workdays" ? value.map(Number) : value,
          },
        },
      };
    });
  };

  const handleAddShiftOverride = () => {
    const unusedMember = allMembers.find(
      (member) => !(shiftSettings.user_overrides || []).some((override) => Number(override.user_id) === Number(member.id)),
    );

    setShiftSettings((current) => ({
      ...current,
      user_overrides: [
        ...(current.user_overrides || []),
        {
          user_id: unusedMember?.id || "",
          start_time: current.defaults.start_time,
          end_time: current.defaults.end_time,
          grace_minutes: current.defaults.grace_minutes,
          workdays: [...(current.defaults.workdays || [])],
        },
      ],
    }));
  };

  const handleShiftOverrideChange = (index, key, value) => {
    setShiftSettings((current) => ({
      ...current,
      user_overrides: (current.user_overrides || []).map((override, overrideIndex) => {
        if (overrideIndex !== index) {
          return override;
        }

        return {
          ...override,
          [key]: key === "workdays" ? value.map(Number) : value,
        };
      }),
    }));
  };

  const handleRemoveShiftOverride = (index) => {
    setShiftSettings((current) => ({
      ...current,
      user_overrides: (current.user_overrides || []).filter((_, overrideIndex) => overrideIndex !== index),
    }));
  };

  const handleSaveAlertSettings = async () => {
    setSavingAlertSettings(true);
    setNotice("");
    setError("");

    try {
      await api.put("/analytics/team-performance/alert-settings", {
        offline_hours_warning: Number(alertSettings.offline_hours_warning),
        longest_offline_stretch_hours_warning: Number(alertSettings.longest_offline_stretch_hours_warning),
        available_without_output_hours_warning: Number(alertSettings.available_without_output_hours_warning),
        status_switches_warning: Number(alertSettings.status_switches_warning),
      });

      setNotice("Performance alert preferences saved.");
      await loadPerformance(days);
      await loadAlerts(true);
    } catch (requestError) {
      console.error("Failed to save alert settings", requestError);
      setError("Could not save performance alert preferences right now.");
    } finally {
      setSavingAlertSettings(false);
    }
  };

  const handleSaveShiftSettings = async () => {
    setSavingShiftSettings(true);
    setNotice("");
    setError("");

    try {
      await api.put("/analytics/team-performance/shift-settings", {
        timezone: shiftSettings.timezone,
        defaults: {
          ...shiftSettings.defaults,
          grace_minutes: Number(shiftSettings.defaults.grace_minutes),
          workdays: (shiftSettings.defaults.workdays || []).map(Number),
        },
        roles: {
          consultant: {
            ...shiftSettings.roles.consultant,
            grace_minutes: Number(shiftSettings.roles.consultant.grace_minutes),
            workdays: (shiftSettings.roles.consultant.workdays || []).map(Number),
          },
          marketing: {
            ...shiftSettings.roles.marketing,
            grace_minutes: Number(shiftSettings.roles.marketing.grace_minutes),
            workdays: (shiftSettings.roles.marketing.workdays || []).map(Number),
          },
        },
        user_overrides: (shiftSettings.user_overrides || []).map((override) => ({
          ...override,
          user_id: Number(override.user_id),
          grace_minutes: Number(override.grace_minutes),
          workdays: (override.workdays || []).map(Number),
        })),
      });

      setNotice("Shift scheduling saved.");
      await loadPerformance(days);
      await loadAlerts(true);
    } catch (requestError) {
      console.error("Failed to save shift settings", requestError);
      setError("Could not save shift scheduling right now.");
    } finally {
      setSavingShiftSettings(false);
    }
  };

  const handleAlertDeliverySettingChange = (key, value) => {
    setAlertDeliverySettings((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const handleSaveAlertDeliverySettings = async () => {
    setSavingAlertDeliverySettings(true);
    setNotice("");
    setError("");

    try {
      await api.put("/analytics/team-performance/alert-delivery-settings", {
        in_app_enabled: Boolean(alertDeliverySettings.in_app_enabled),
        email_enabled: Boolean(alertDeliverySettings.email_enabled),
        late_start_days_warning: Number(alertDeliverySettings.late_start_days_warning),
        no_activity_shift_days_warning: Number(alertDeliverySettings.no_activity_shift_days_warning),
        productivity_drop_percent_warning: Number(alertDeliverySettings.productivity_drop_percent_warning),
      });

      setNotice("Alert delivery settings saved.");
      await loadPerformance(days);
      await loadAlerts(true);
    } catch (requestError) {
      console.error("Failed to save alert delivery settings", requestError);
      setError("Could not save alert delivery settings right now.");
    } finally {
      setSavingAlertDeliverySettings(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/analytics/team-performance/alerts/${alertId}/acknowledge`);
      setLiveAlerts((current) =>
        current.map((alertItem) =>
          alertItem.id === alertId
            ? { ...alertItem, acknowledged_at: new Date().toISOString() }
            : alertItem,
        ),
      );
    } catch (requestError) {
      console.error("Failed to acknowledge alert", requestError);
      setError("Could not acknowledge the selected alert right now.");
    }
  };

  const handleAcknowledgeAllAlerts = async () => {
    setAcknowledgingAllAlerts(true);
    setNotice("");
    setError("");

    try {
      const response = await api.post("/analytics/team-performance/alerts/acknowledge-all");
      const acknowledgedCount = Number(response.data?.data?.acknowledged_count || 0);
      const acknowledgedAt = new Date().toISOString();

      setLiveAlerts((current) =>
        current.map((alertItem) =>
          alertItem.acknowledged_at ? alertItem : { ...alertItem, acknowledged_at: acknowledgedAt },
        ),
      );
      setNotice(
        acknowledgedCount > 0
          ? `${acknowledgedCount} performance alert${acknowledgedCount === 1 ? "" : "s"} acknowledged.`
          : "No open performance alerts needed acknowledgement.",
      );
    } catch (requestError) {
      console.error("Failed to acknowledge all alerts", requestError);
      setError("Could not acknowledge all open alerts right now.");
    } finally {
      setAcknowledgingAllAlerts(false);
    }
  };

  const consultantTrend = consultants?.trend || [];
  const marketingTrend = marketing?.trend || [];
  const managerTrend = consultantTrend.map((row, index) => {
    const peer = marketingTrend[index] || {};

    return {
      label: row.label,
      consultant_handled:
        Number(row.email_replies || 0) +
        Number(row.whatsapp_replies || 0) +
        Number(row.calls_handled || 0) +
        Number(row.closures || 0),
      marketing_handled:
        Number(peer.email_replies || 0) +
        Number(peer.whatsapp_replies || 0) +
        Number(peer.calls_handled || 0) +
        Number(peer.closures || 0),
      consultant_customer_touches: Number(row.customer_touches || 0),
      marketing_customer_touches: Number(peer.customer_touches || 0),
      consultant_campaign_output: Number(row.campaign_output || 0),
      marketing_campaign_output: Number(peer.campaign_output || 0),
      consultant_offline_hours: Number(row.offline_hours || 0),
      marketing_offline_hours: Number(peer.offline_hours || 0),
      consultant_scheduled_shift_hours: Number(row.scheduled_shift_hours || 0),
      marketing_scheduled_shift_hours: Number(peer.scheduled_shift_hours || 0),
      consultant_late_starts: Number(row.late_starts || 0),
      marketing_late_starts: Number(peer.late_starts || 0),
    };
  });

  const renderTeamView = (group, label) => (
    <Stack spacing={3}>
      <Box sx={SUMMARY_GRID_SX}>
        <SummaryCard
          label="Team Members"
          value={formatNumber(group?.summary?.members)}
          subtitle={`Tracked people in ${label.toLowerCase()}`}
          accent="#0f766e"
          icon={<PeopleAltIcon />}
        />
        <SummaryCard
          label="Messages Answered"
          value={formatNumber(Number(group?.summary?.email_replies || 0) + Number(group?.summary?.whatsapp_replies || 0))}
          subtitle={`Replies across the last ${days} days`}
          accent={COLORS.whatsapp}
          icon={<BoltIcon />}
        />
        <SummaryCard
          label="Closures"
          value={formatNumber(group?.summary?.closures)}
          subtitle="Resolved or closed workload"
          accent={COLORS.closures}
          icon={<TaskAltIcon />}
        />
        <SummaryCard
          label="Customer Touches"
          value={formatNumber(group?.summary?.customer_touches)}
          subtitle={`Outbound customer actions tracked in the last ${days} days`}
          accent="#2563eb"
          icon={<PeopleAltIcon />}
        />
        <SummaryCard
          label="Campaign Output"
          value={formatNumber(group?.summary?.campaign_output_total)}
          subtitle="Mass email and WhatsApp recipients sent"
          accent={COLORS.campaign}
          icon={<CampaignIcon />}
        />
        <SummaryCard
          label="Productive Presence"
          value={formatHours(group?.summary?.productive_presence_hours)}
          subtitle="Time spent online in this window"
          accent={COLORS.availability}
          icon={<AccessTimeIcon />}
        />
        <SummaryCard
          label="Shift Adherence"
          value={`${Number(group?.summary?.avg_shift_adherence_rate || 0).toFixed(1)}%`}
          subtitle={`${formatHours(group?.summary?.scheduled_shift_hours)} scheduled across this window`}
          accent="#2563eb"
          icon={<AccessTimeIcon />}
        />
        <SummaryCard
          label="Late Starts"
          value={formatNumber(group?.summary?.late_starts)}
          subtitle={`${formatNumber(group?.summary?.no_activity_shift_days)} no-activity shift days`}
          accent="#dc2626"
          icon={<QueryStatsIcon />}
        />
      </Box>

      <Grid container spacing={2.5} alignItems="stretch">
        <Grid item xs={12}>
          <TrendChart
            title={`${label} productivity trend`}
            description="Replies, calls, closures, and campaign output are trended together for better manager visibility."
            data={group?.trend || []}
            lines={[
              { key: "email_replies", label: "Email", color: COLORS.email },
              { key: "whatsapp_replies", label: "WhatsApp", color: COLORS.whatsapp },
              { key: "calls_handled", label: "Calls", color: COLORS.calls },
              { key: "closures", label: "Closures", color: COLORS.closures },
              { key: "customer_touches", label: "Customer touches", color: "#2563eb", strokeWidth: 2.5 },
              { key: "campaign_output", label: "Campaign output", color: COLORS.campaign, strokeWidth: 2.5 },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
        <TrendChart
          title={`${label} presence trend`}
          description="Online time, offline time, and status switching are trended together for presence and staffing insight."
          data={group?.trend || []}
          lines={[
            { key: "available_hours", label: "Available hours", color: COLORS.availability, strokeWidth: 2.5 },
            { key: "scheduled_shift_hours", label: "Scheduled shift hours", color: "#2563eb", strokeWidth: 2, dot: false },
            { key: "offline_hours", label: "Offline hours", color: "#64748b", dashed: true, strokeWidth: 2.5 },
            { key: "status_switches", label: "Status switches", color: "#7c3aed", strokeWidth: 2, dot: false },
            { key: "late_starts", label: "Late starts", color: "#dc2626", strokeWidth: 2, dot: false },
            { key: "no_activity_shift_days", label: "No-activity shift days", color: "#0f766e", strokeWidth: 2, dot: false },
          ]}
          />
        </Grid>
      </Grid>

      <Stack spacing={2.5}>
        <StatusBreakdown statusCounts={group?.summary?.status_counts || {}} />
        <ActivityFeed
          title="Recent activity"
          description="Latest actions captured for this team inside the reporting window."
          items={group?.recent_activity || []}
        />
      </Stack>

      <WatchlistsSection watchlists={group?.watchlists || {}} onSelectMember={openMember} />

      <ProductivityTable
        title="Full team productivity table"
        description="Every member is listed here so managers can inspect output, availability, campaign volume, and recent activity in one place."
        members={group?.members || []}
        onSelectMember={openMember}
      />
    </Stack>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "none",
        p: { xs: 2, md: 3 },
        "& .MuiChip-root": {
          pointerEvents: "none",
          cursor: "default",
        },
      }}
    >
      <Paper
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: "24px",
          mb: 3,
          background: "linear-gradient(135deg, #f4fbf6 0%, #f8fafc 52%, #eff6ff 100%)",
          overflow: "hidden",
          position: "relative",
          border: PANEL_BORDER,
          boxShadow: CARD_SHADOW,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: "auto -60px -90px auto",
            width: 240,
            height: 240,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(34,197,94,0.14), rgba(34,197,94,0))",
          }}
        />
        <Stack spacing={2.5}>
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5} justifyContent="space-between">
            <Box sx={{ width: "100%" }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.25 }}>
                <Typography variant="h4" sx={{ fontWeight: 900 }}>
                  Performance
                </Typography>
                <Chip
                  label="Phase 6"
                  size="small"
                  sx={{
                    bgcolor: "rgba(34,197,94,0.12)",
                    color: "#15803d",
                    fontWeight: 800,
                  }}
                />
              </Stack>
              <Typography variant="body1" sx={{ color: "text.secondary", maxWidth: 720 }}>
                This manager view now shows the whole team, not only top performers. Track replies, claims, closures,
                campaign output, live availability, presence discipline, watchlists, and individual productivity profiles from one place.
              </Typography>
              {payload?.meta?.generated_at && (
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.4 }}>
                  Last refreshed {formatDateTime(payload.meta.generated_at)}
                </Typography>
              )}
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
              <TextField
                select
                size="small"
                label="Window"
                value={days}
                onChange={(event) => setDays(Number(event.target.value))}
                sx={{ minWidth: 120 }}
              >
                {DAY_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>
                    Last {option} days
                  </MenuItem>
                ))}
              </TextField>

              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadPerformance(days)} disabled={loading} sx={{ borderRadius: "15px" }}>
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <BoltIcon />}
                onClick={handleSync}
                disabled={syncing || loading}
                sx={{ borderRadius: "15px", bgcolor: "#4f46e5", "&:hover": { bgcolor: "#4338ca" } }}
              >
                {syncing ? "Rebuilding…" : "Rebuild Data"}
              </Button>
            </Stack>
          </Stack>

          {syncResult && (
            <Alert
              severity={syncResult.success ? "success" : "error"}
              onClose={() => setSyncResult(null)}
              sx={{ borderRadius: "12px" }}
            >
              {syncResult.message}
            </Alert>
          )}

          <Paper
            sx={{
              p: 2,
              borderRadius: INNER_RADIUS,
              bgcolor: "rgba(255,255,255,0.75)",
              border: "1px solid rgba(148,163,184,0.18)",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                alignItems: "end",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "minmax(260px, 1.35fr) repeat(2, minmax(220px, 1fr)) auto",
                },
              }}
            >
              <TextField
                select
                size="small"
                fullWidth
                label="Team member"
                value={reportUserId}
                onChange={(event) => setReportUserId(event.target.value)}
              >
                <MenuItem value="">All team members</MenuItem>
                {scopedMembers.map((member) => (
                  <MenuItem key={member.id} value={String(member.id)}>
                    {member.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" fullWidth label="Channel" value={channelFilter} onChange={(event) => setChannelFilter(event.target.value)}>
                {REPORT_CHANNELS.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "all" ? "All channels" : option.charAt(0).toUpperCase() + option.slice(1).replace("_", " ")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField select size="small" fullWidth label="Module" value={moduleFilter} onChange={(event) => setModuleFilter(event.target.value)}>
                {REPORT_MODULES.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option === "all" ? "All modules" : option.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())}
                  </MenuItem>
                ))}
              </TextField>
              <Stack
                spacing={0.6}
                alignItems={{ xs: "flex-start", lg: "flex-end" }}
                sx={{
                  justifySelf: { xs: "stretch", lg: "end" },
                  minWidth: { lg: 220 },
                }}
              >
                <Chip label={exportMode} size="small" sx={{ fontWeight: 700 }} />
                <Button
                  variant="contained"
                  startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlinedIcon />}
                  onClick={() => handleDownload()}
                  disabled={downloading || loading}
                  sx={{
                    borderRadius: "15px",
                    bgcolor: "#16a34a",
                    "&:hover": { bgcolor: "#15803d" },
                  }}
                >
                  Generate Report
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Stack>
      </Paper>

      {/* ─── Live Now Panel ─────────────────────────────────────────────── */}
      <Paper
        sx={{
          mb: 3,
          p: { xs: 2, md: 2.5 },
          borderRadius: "20px",
          border: "1px solid rgba(99,102,241,0.18)",
          background: "linear-gradient(135deg, #f0f9ff 0%, #fefce8 100%)",
          boxShadow: CARD_SHADOW,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: "auto -40px -60px auto",
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.10), rgba(99,102,241,0))",
            pointerEvents: "none",
          }}
        />
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <CircleIcon sx={{ color: "#22c55e", fontSize: 12, animation: "pulse 2s infinite" }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: "-0.3px" }}>
            Live Now
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {liveStats?.as_of ? `as of ${new Date(liveStats.as_of).toLocaleTimeString()}` : "loading…"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={loadLiveStats} disabled={liveStatsLoading} sx={{ opacity: 0.6 }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>

        {liveStatsLoading && !liveStats ? (
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            {[1,2,3,4,5,6].map((i) => <Skeleton key={i} variant="rounded" width={140} height={70} sx={{ borderRadius: "12px" }} />)}
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 1.5,
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(4, 1fr)",
                md: "repeat(7, 1fr)",
              },
            }}
          >
            {[
              {
                label: "Open Conversations",
                value: liveStats?.open_conversations ?? "—",
                sub: `${liveStats?.unassigned ?? 0} unassigned`,
                color: "#4f46e5",
                warn: (liveStats?.unassigned ?? 0) > 5,
              },
              {
                label: "Email Open",
                value: liveStats?.by_channel?.email ?? "—",
                sub: "channel inbox",
                color: "#0ea5e9",
              },
              {
                label: "WhatsApp Open",
                value: liveStats?.by_channel?.whatsapp ?? "—",
                sub: "channel inbox",
                color: "#16a34a",
              },
              {
                label: "Replies Today",
                value: liveStats?.today?.replies_sent ?? "—",
                sub: "sent by team",
                color: "#7c3aed",
              },
              {
                label: "Inquiries Today",
                value: liveStats?.today?.inquiries_received ?? "—",
                sub: "received",
                color: "#b45309",
              },
              {
                label: "Closures Today",
                value: liveStats?.today?.closures ?? "—",
                sub: `${liveStats?.today?.claims ?? 0} claims`,
                color: "#059669",
              },
              {
                label: "SLA Breached",
                value: liveStats?.sla_breached ?? "—",
                sub: "no activity 24h+",
                color: "#dc2626",
                warn: (liveStats?.sla_breached ?? 0) > 0,
              },
            ].map((stat) => (
              <Paper
                key={stat.label}
                sx={{
                  p: 1.5,
                  borderRadius: "14px",
                  border: stat.warn ? "1.5px solid #f97316" : "1px solid rgba(148,163,184,0.15)",
                  bgcolor: stat.warn ? "rgba(249,115,22,0.06)" : "rgba(255,255,255,0.85)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: stat.color, lineHeight: 1 }}
                >
                  {stat.value}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 700, color: "text.primary", display: "block", mt: 0.4 }}>
                  {stat.label}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
                  {stat.sub}
                </Typography>
              </Paper>
            ))}
          </Box>
        )}

        {/* Team online status row */}
        {(liveStats?.team_availability?.online_now?.length > 0 || liveStats?.team_availability?.counts) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid rgba(148,163,184,0.15)" }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} flexWrap="wrap">
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", minWidth: 80 }}>
                Team Status
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Object.entries({
                  available: { label: "Available", color: "#22c55e" },
                  offline: { label: "Offline", color: "#cbd5e1" },
                }).map(([key, { label, color }]) => {
                  const count = liveStats?.team_availability?.counts?.[key] ?? 0;
                  if (count === 0) return null;
                  return (
                    <Chip
                      key={key}
                      size="small"
                      label={`${label}: ${count}`}
                      sx={{
                        bgcolor: `${color}20`,
                        color: key === "offline" ? "#64748b" : color,
                        fontWeight: 700,
                        fontSize: "0.72rem",
                        border: `1px solid ${color}40`,
                      }}
                    />
                  );
                })}
              </Stack>
              {liveStats?.team_availability?.online_now?.length > 0 && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ ml: { sm: "auto" } }}>
                  {liveStats.team_availability.online_now.slice(0, 8).map((member) => (
                    <Chip
                      key={member.id}
                      size="small"
                      avatar={<Avatar sx={{ bgcolor: member.status === "available" ? "#22c55e" : "#f59e0b", width: 18, height: 18, fontSize: "0.6rem" }}>{member.name?.[0]}</Avatar>}
                      label={member.name?.split(" ")[0]}
                      sx={{ fontSize: "0.72rem", fontWeight: 600 }}
                    />
                  ))}
                  {liveStats.team_availability.online_now.length > 8 && (
                    <Chip size="small" label={`+${liveStats.team_availability.online_now.length - 8} more`} sx={{ fontSize: "0.72rem" }} />
                  )}
                </Stack>
              )}
            </Stack>
          </Box>
        )}
      </Paper>

      {/* ─── Snapshot Summary Grid (historical window) ─────────────────── */}
      <Box sx={{ ...SUMMARY_GRID_SX, mb: 3 }}>
        <SummaryCard
          label="Messages Answered"
          value={formatNumber(Number(overall?.summary?.email_replies || 0) + Number(overall?.summary?.whatsapp_replies || 0))}
          subtitle="Email and WhatsApp replies in the selected window"
          accent={COLORS.whatsapp}
          icon={<BoltIcon />}
        />
        <SummaryCard
          label="Calls Handled"
          value={formatNumber(overall?.summary?.calls_handled)}
          subtitle="Tracked voice workload across the unified inbox"
          accent={COLORS.calls}
          icon={<PhoneInTalkIcon />}
        />
        <SummaryCard
          label="Closures"
          value={formatNumber(overall?.summary?.closures)}
          subtitle="Conversations and inquiries closed by the team"
          accent={COLORS.closures}
          icon={<TaskAltIcon />}
        />
        <SummaryCard
          label="Most Available"
          value={overall?.summary?.most_available?.name || "No leader yet"}
          subtitle={overall?.summary?.most_available?.display_value || "Availability data will appear here"}
          accent={COLORS.availability}
          icon={<AccessTimeIcon />}
        />
      </Box>

      <Paper sx={{ p: 1, borderRadius: INNER_RADIUS, mb: 3, border: PANEL_BORDER, boxShadow: CARD_SHADOW }}>
        <Tabs
          value={tab}
          onChange={(_, nextTab) => setTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: 999,
              bgcolor: "#16a34a",
            },
          }}
        >
          {TABS.map((item) => (
            <Tab key={item.value} value={item.value} icon={item.icon} iconPosition="start" label={item.label} sx={{ minHeight: 60, fontWeight: 700 }} />
          ))}
        </Tabs>
      </Paper>

      {loading && (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={320} />
          <Skeleton variant="rounded" height={320} />
        </Stack>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && notice && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {notice}
        </Alert>
      )}

      {!loading && !error && !payload && <Alert severity="info">No performance data is available yet.</Alert>}

      {!loading && !error && payload && tab === "manager" && (
        <Stack spacing={3}>
          <AlertSettingsCard
            settings={alertSettings}
            saving={savingAlertSettings}
            onChange={handleAlertSettingChange}
            onSave={handleSaveAlertSettings}
          />
          <AlertDeliverySettingsCard
            settings={alertDeliverySettings}
            saving={savingAlertDeliverySettings}
            onChange={handleAlertDeliverySettingChange}
            onSave={handleSaveAlertDeliverySettings}
          />
          <ShiftSettingsCard
            settings={shiftSettings}
            members={allMembers}
            saving={savingShiftSettings}
            onTimezoneChange={handleShiftTimezoneChange}
            onProfileChange={handleShiftProfileChange}
            onOverrideProfileChange={handleShiftOverrideChange}
            onAddOverride={handleAddShiftOverride}
            onRemoveOverride={handleRemoveShiftOverride}
            onSave={handleSaveShiftSettings}
          />
          <LiveAlertsPanel
            alerts={liveAlerts}
            loading={alertsLoading}
            onAcknowledge={handleAcknowledgeAlert}
            onAcknowledgeAll={handleAcknowledgeAllAlerts}
            acknowledgingAll={acknowledgingAllAlerts}
          />

          <Box sx={DASHBOARD_GRID_SX}>
            <SummaryCard
              label="Tracked Members"
              value={formatNumber(overall?.summary?.members)}
              subtitle="Consultants and marketing included in this comparison"
              accent="#0f766e"
              icon={<PeopleAltIcon />}
            />
            <SummaryCard
              label="Campaign Output"
              value={formatNumber(overall?.summary?.campaign_output_total)}
              subtitle="Mass email plus WhatsApp recipients sent"
              accent={COLORS.campaign}
              icon={<CampaignIcon />}
            />
            <SummaryCard
              label="Customer Touches"
              value={formatNumber(overall?.summary?.customer_touches)}
              subtitle="Tracked outbound customer contact actions"
              accent="#2563eb"
              icon={<PeopleAltIcon />}
            />
            <SummaryCard
              label="Presence Hours"
              value={formatHours(overall?.summary?.available_hours)}
              subtitle="Automatic presence accumulated from live sync"
              accent={COLORS.availability}
              icon={<AccessTimeIcon />}
            />
            <SummaryCard
              label="Productive Presence"
              value={formatHours(overall?.summary?.productive_presence_hours)}
              subtitle="Time spent online across the team"
              accent="#0f766e"
              icon={<AccessTimeIcon />}
            />
            <SummaryCard
              label="Longest Offline"
              value={overall?.summary?.longest_offline_stretch?.name || "No leader yet"}
              subtitle={overall?.summary?.longest_offline_stretch?.display_value || "Offline stretch data will appear here"}
              accent="#64748b"
              icon={<AccessTimeIcon />}
            />
            <SummaryCard
              label="Activity Events"
              value={formatNumber(overall?.summary?.activity_events)}
              subtitle="Tracked actions written into the new activity ledger"
              accent="#8b5cf6"
              icon={<QueryStatsIcon />}
            />
            <SummaryCard
              label="Shift Adherence"
              value={`${Number(overall?.summary?.avg_shift_adherence_rate || 0).toFixed(1)}%`}
              subtitle={`${formatHours(overall?.summary?.scheduled_shift_hours)} scheduled this window`}
              accent="#2563eb"
              icon={<AccessTimeIcon />}
            />
            <SummaryCard
              label="Late Starts"
              value={formatNumber(overall?.summary?.late_starts)}
              subtitle={`${formatNumber(overall?.summary?.no_activity_shift_days)} no-activity shift days`}
              accent="#dc2626"
              icon={<QueryStatsIcon />}
            />
            <SummaryCard
              label="Open Alerts"
              value={formatNumber(liveAlerts.length)}
              subtitle="Persisted manager alerts awaiting review"
              accent="#be123c"
              icon={<BoltIcon />}
            />
          </Box>

          <Box sx={{ display: "grid", gap: 2.5 }}>
            <TrendChart
              title="Cross-team comparison"
              description="Handled workload, campaign volume, and customer touches are compared between consultants and marketing."
              data={managerTrend}
              lines={[
                { key: "consultant_handled", label: "Consultant handled", color: COLORS.consultants },
                { key: "marketing_handled", label: "Marketing handled", color: COLORS.marketing },
                { key: "consultant_customer_touches", label: "Consultant touches", color: "#86b7fe", dashed: true, strokeWidth: 2.5, dot: false },
                { key: "marketing_customer_touches", label: "Marketing touches", color: "#1d4ed8", dashed: true, strokeWidth: 2.5, dot: false },
                { key: "consultant_campaign_output", label: "Consultant campaign output", color: "#86efac", dashed: true, strokeWidth: 2.5, dot: false },
                { key: "marketing_campaign_output", label: "Marketing campaign output", color: "#93c5fd", dashed: true, strokeWidth: 2.5, dot: false },
              ]}
            />
            <TrendChart
              title="Cross-team presence trend"
              description="Availability discipline is compared across consultants and marketing using session-based presence data."
              data={managerTrend}
              lines={[
                { key: "consultant_offline_hours", label: "Consultant offline", color: "#94a3b8", dashed: true, strokeWidth: 2.5, dot: false },
                { key: "marketing_offline_hours", label: "Marketing offline", color: "#475569", dashed: true, strokeWidth: 2.5, dot: false },
              { key: "consultant_scheduled_shift_hours", label: "Consultant scheduled", color: "#16a34a", strokeWidth: 2, dot: false },
              { key: "marketing_scheduled_shift_hours", label: "Marketing scheduled", color: "#2563eb", strokeWidth: 2, dot: false },
              { key: "consultant_late_starts", label: "Consultant late starts", color: "#dc2626", strokeWidth: 2, dot: false },
              { key: "marketing_late_starts", label: "Marketing late starts", color: "#7f1d1d", strokeWidth: 2, dot: false },
            ]}
          />
          <Stack spacing={2.5}>
            <StatusBreakdown statusCounts={overall?.summary?.status_counts || {}} />
            <ActivityFeed
              title="Cross-team activity feed"
              description="Latest tracked activity across consultants and marketing for this window."
              items={overall?.recent_activity || []}
            />
          </Stack>
          </Box>

          <WatchlistsSection watchlists={overall?.watchlists || {}} onSelectMember={openMember} />

          <ProductivityTable
            title="All-team productivity table"
            description="Managers can inspect every tracked team member here, not only top performers."
            members={allMembers}
            onSelectMember={openMember}
          />
        </Stack>
      )}

      {!loading && !error && payload && tab === "consultants" && renderTeamView(consultants, "Consultants")}
      {!loading && !error && payload && tab === "marketing" && renderTeamView(marketing, "Marketing")}
      {!loading && !error && payload && tab === "timeline" && <ActivityTimeline />}

      <DetailDrawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        detail={memberDetail}
        loading={memberLoading}
        selectedMember={selectedMember}
        onExport={() => handleDownload({ userId: selectedMember?.id })}
      />
    </Box>
  );
}
